import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Tabs,
    Tab,
    Typography,
    Box,
    Snackbar,
    Alert,
    Grid,
} from "@mui/material";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';

import Overview from './Overview';
import PlanSelectionPage from './PlanSelectionPage';
import PaymentHistory from './PaymentHistory';
import PaymentMethod from './PaymentMethod';
import SubscriptionLog from "./SubscriptionLog";
import InvoiceHistory from './InvoiceHistory';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

const tabSlugs = ['overview', 'cards', 'plan', 'subscriptions', 'payments', 'invoices'];

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: '100%' }}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        "aria-controls": `vertical-tabpanel-${index}`,
    };
}

export default function Billing() {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'Billing | KlonIT Web Application';

        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                const role = user?.active_team?.user_role_in_team || "";
                if (role.toLowerCase() !== "admin") {
                    navigate("/chatbots", { replace: true });
                }
            } catch (err) {
                navigate("/chatbots", { replace: true });
            }
        } else {
            navigate("/chatbots", { replace: true });
        }
    }, [navigate]);

    const [searchParams, setSearchParams] = useSearchParams();
    const getTabIndexFromUrl = useCallback(() => {
        const tabQuery = searchParams.get('tab')?.toLowerCase();
        if (tabQuery) {
            const index = tabSlugs.indexOf(tabQuery);
            if (index !== -1) return index;
        }
        return 0;
    }, [searchParams]);

    const [value, setValue] = useState(getTabIndexFromUrl);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    useEffect(() => {
        const indexFromUrl = getTabIndexFromUrl();
        if (indexFromUrl !== value) {
            setValue(indexFromUrl);
        }
        const currentTabSlugInUrl = searchParams.get('tab')?.toLowerCase();
        if (!currentTabSlugInUrl || tabSlugs.indexOf(currentTabSlugInUrl) === -1) {
            setSearchParams({ tab: tabSlugs[value] }, { replace: true });
        }
    }, [searchParams, value, getTabIndexFromUrl, setSearchParams]);

    const handleChange = (event, newValue) => {
        if (newValue !== value) {
            setValue(newValue);
            const newSlug = tabSlugs[newValue];
            setSearchParams({ tab: newSlug });
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const showMessage = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handlePlanChangeComplete = useCallback((successMessage) => {
        showMessage(successMessage || "Subscription updated successfully!", "success");
        const overviewIndex = tabSlugs.indexOf('overview');
        if (overviewIndex !== -1) {
            setValue(overviewIndex);
            setSearchParams({ tab: 'overview' });
        }
    }, [setSearchParams]);

    const handlePlanChangeError = useCallback((errorMessage) => {
        showMessage(errorMessage || "An error occurred while updating the subscription.", "error");
    }, []);

    const tabItems = [
        { label: 'Overview', icon: <ReceiptLongIcon sx={{ fontSize: '1.1rem' }} /> },
        { label: 'Payment Methods', icon: <CreditCardIcon sx={{ fontSize: '1.1rem' }} /> },
        { label: 'Manage Plan', icon: <WorkspacePremiumIcon sx={{ fontSize: '1.1rem' }} /> },
        { label: 'Subscription History', icon: <HistoryIcon sx={{ fontSize: '1.1rem' }} /> },
        { label: 'Payment History', icon: <PaymentsIcon sx={{ fontSize: '1.1rem' }} /> },
        { label: 'Invoices', icon: <ReceiptLongIcon sx={{ fontSize: '1.1rem' }} /> },
    ];

    return (
        <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
                }}>
                    <ReceiptLongIcon sx={{ color: '#fff', fontSize: 28 }} />
                </Box>
                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
                    Billing Management
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.875rem', color: colors.textTertiary, mt: 0.5 }}>
                    Manage your subscription, payment methods, and billing history
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Sidebar Tabs */}
                <Grid item xs={12} md={3}>
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        p: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 1 }}>
                            Billing
                        </Typography>
                        <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={value}
                            onChange={handleChange}
                            sx={{
                                '& .MuiTab-root': {
                                    fontFamily,
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    minHeight: 44,
                                    justifyContent: 'flex-start',
                                    px: 2,
                                    borderRadius: '10px',
                                    color: colors.textSecondary,
                                    '&:hover': { bgcolor: 'rgba(16,185,129,0.08)' },
                                    '&.Mui-selected': {
                                        color: '#059669',
                                        bgcolor: 'rgba(16,185,129,0.1)',
                                        fontWeight: 600,
                                    },
                                },
                                '& .MuiTabs-indicator': { display: 'none' },
                            }}
                        >
                            {tabItems.map((item, index) => (
                                <Tab
                                    key={index}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                            {item.icon}
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{item.label}</Typography>
                                        </Box>
                                    }
                                    {...a11yProps(index)}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Grid>

                {/* Main Content */}
                <Grid item xs={12} md={9}>
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        p: 3,
                        border: '1px solid rgba(0,0,0,0.06)',
                        minHeight: 400,
                    }}>
                        <TabPanel value={value} index={0}>
                            <Overview key="overview-tab" />
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <PaymentMethod />
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                            <PlanSelectionPage
                                onComplete={handlePlanChangeComplete}
                                onError={handlePlanChangeError}
                            />
                        </TabPanel>
                        <TabPanel value={value} index={3}>
                            <SubscriptionLog />
                        </TabPanel>
                        <TabPanel value={value} index={4}>
                            <PaymentHistory />
                        </TabPanel>
                        <TabPanel value={value} index={5}>
                            <InvoiceHistory />
                        </TabPanel>
                    </Box>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', fontFamily, borderRadius: '12px' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}
